import React, { useState } from 'react';
import { OnboardingFormData } from '..';
import { MapPin, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usersApi } from '@/services/users';
import { useAtomValue } from 'jotai';
import { authStateAtom } from '@/stores/auth.atom';
import { toast } from '@/components/ui/use-toast';

interface NeighborhoodStepProps {
  formData: OnboardingFormData;
  updateFormData: (data: Partial<OnboardingFormData>) => void;
  onNext: () => void;
  onSkip: () => void;
}

const NeighborhoodStep: React.FC<NeighborhoodStepProps> = ({
  updateFormData,
  onSkip
}) => {
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [locationGranted, setLocationGranted] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const authState = useAtomValue(authStateAtom);

  const requestLocation = async () => {
    setIsRequestingLocation(true);
    setLocationError(null);

    try {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        throw new Error('Trình duyệt không hỗ trợ định vị');
      }

      // Request location permission
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000
          }
        );
      });

      const { latitude, longitude } = position.coords;

      // Update form data with location
      updateFormData({
        location: { latitude, longitude }
      });

      // Save location to backend if user is authenticated
      if (authState.user?.id) {
        try {
          await usersApi.updateUser(authState.user.id, {
            location: { latitude, longitude }
          });

          toast({
            title: "Thành công!",
            description: "Vị trí của bạn đã được lưu.",
          });
        } catch (error) {
          console.error('Failed to save location to backend:', error);
          // Don't show error to user, just log it
        }
      }

      setLocationGranted(true);

    } catch (error: unknown) {
      let errorMessage = 'Không thể lấy vị trí của bạn';

      if (error && typeof error === 'object' && 'code' in error) {
        const geolocationError = error as GeolocationPositionError;
        if (geolocationError.code === 1) {
          errorMessage = 'Bạn đã từ chối chia sẻ vị trí';
        } else if (geolocationError.code === 2) {
          errorMessage = 'Không thể xác định vị trí';
        } else if (geolocationError.code === 3) {
          errorMessage = 'Hết thời gian chờ';
        }
      }

      setLocationError(errorMessage);

      toast({
        variant: "destructive",
        title: "Lỗi định vị",
        description: errorMessage,
      });
    } finally {
      setIsRequestingLocation(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center px-4 py-6 space-y-6 bg-pure-white min-h-full">
      {/* Illustration */}
      <div className="flex flex-col justify-center items-center gap-2.5">
        <div className="w-24 h-24 bg-burnt-orange/10 rounded-full flex items-center justify-center">
          <MapPin className="w-12 h-12 text-burnt-orange" />
        </div>
      </div>

      {/* Title */}
      <div className="text-center">
        <h1 className="text-2xl font-semibold font-manrope text-midnight-blue leading-7">
          Chia Sẻ Vị Trí <br/>Của Bạn
        </h1>
      </div>

      {/* Content */}
      <div className="text-center max-w-sm">
        <p className="text-sm font-normal font-sora text-midnight-blue/60 leading-relaxed">
          Để kết nối với hàng xóm gần bạn và khám phá những điều thú vị trong khu vực,
          hãy cho phép Locuno truy cập vị trí của bạn.
        </p>
      </div>

      {/* Location Status */}
      {locationGranted && (
        <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-2 rounded-full">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Vị trí đã được lưu</span>
        </div>
      )}

      {locationError && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-2 rounded-full">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm font-medium">{locationError}</span>
        </div>
      )}

      {/* Action Button */}
      {!locationGranted && (
        <Button
          onClick={requestLocation}
          disabled={isRequestingLocation}
          className="h-11 px-8 py-3.5 bg-burnt-orange hover:bg-burnt-orange/90 rounded-full text-pure-white font-semibold"
        >
          {isRequestingLocation ? 'Đang lấy vị trí...' : 'Cho Phép Truy Cập Vị Trí'}
        </Button>
      )}

      {/* Info Card */}
      <div className="w-full max-w-sm px-4 pt-6 pb-6 bg-pure-white rounded-lg shadow-sm outline outline-1 outline-burnt-orange flex flex-col items-center gap-4">
        <div className="flex items-center gap-2 text-center text-burnt-orange text-base font-semibold font-sora">
          <span>🔒</span>
          Quyền riêng tư được bảo vệ
          <span>🔒</span>
        </div>

        <div className="flex flex-col items-center gap-2.5 text-center">
          <p className="text-sm font-normal font-sora text-midnight-blue/60">
            • Vị trí chỉ được sử dụng để tìm hàng xóm gần bạn
          </p>
          <p className="text-sm font-normal font-sora text-midnight-blue/60">
            • Bạn có thể tắt chia sẻ vị trí bất cứ lúc nào
          </p>
          <p className="text-sm font-normal font-sora text-midnight-blue/60">
            • Thông tin được mã hóa và bảo mật tuyệt đối
          </p>
        </div>
      </div>

      {/* Skip option */}
      <button
        onClick={onSkip}
        className="text-sm text-midnight-blue/60 hover:text-burnt-orange transition-colors"
      >
        Bỏ qua bước này
      </button>
    </div>
  );
};

export default NeighborhoodStep;